import { useMemo, useState } from 'preact/hooks';
import type { PageProps } from '../app';
import { isActiveLog, STATE_ORDER, type LogDescriptor, type LogKind } from '../core/log/client';
import { PROGRAM_LABEL } from '../core/loglist/sources';
import { href, navigate } from '../router';
import { ErrorBox } from '../ui/components/ErrorBox';
import { Icon } from '../ui/components/Icon';
import { KindBadge, ProgramBadges } from '../ui/components/LogBadges';
import { Spinner } from '../ui/components/Spinner';
import { fmtRelative } from '../ui/format';
import { addCustomLog, ensureRegistry, registry, registryState, removeCustomLog } from '../ui/state/logs';
import { settings, updateSettings } from '../ui/state/settings';
import { hasProxy, proxyOffer } from '../ui/state/transport';
import { collapsedOperators, setAllOperators, toggleOperator } from '../ui/state/groups';
import { UrlCompact } from '../ui/components/UrlCompact';
import { usePageState } from '../ui/state/pageState';
import { logPath, logRoute, operatorPath } from '../ui/nav';
import { Breadcrumbs } from '../ui/components/Breadcrumbs';
import { Segmented } from '../ui/components/Segmented';
import { isDesktop } from '../ui/state/desktop';

export function Home({ params }: PageProps) {
  const st = registryState.value;
  const reg = registry.value;
  // `#/logs/<op>` shows a single operator; the slug resolves once the lists are loaded.
  const operator = params.op !== undefined ? reg.operatorBySlug(params.op) : undefined;
  const [q, setQ] = usePageState('home:q', '');
  const [kind, setKind] = usePageState<LogKind | 'all'>('home:kind', 'all');
  const [showAdd, setShowAdd] = useState(false);
  const showRetired = settings.value.showRetired;

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out: Array<[string, LogDescriptor[]]> = [];
    for (const [op, logs] of reg.byOperator()) {
      if (operator !== undefined && op !== operator) continue;
      const filtered = logs
        .filter((l) => kind === 'all' || l.kind === kind)
        .filter((l) => showRetired || isActiveLog(l))
        .filter((l) => !needle || `${l.description} ${l.url} ${l.monitoringUrl} ${op}`.toLowerCase().includes(needle))
        .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state) || a.description.localeCompare(b.description));
      if (filtered.length) out.push([op, filtered]);
    }
    return out;
  }, [reg, q, kind, showRetired]);
  const total = groups.reduce((n, [, l]) => n + l.length, 0);
  const all = reg.all().length;
  const active = reg.all().filter(isActiveLog).length;
  const collapsed = collapsedOperators.value;
  const searching = q.trim() !== '';
  const allCollapsed = groups.length > 0 && groups.every(([op]) => collapsed.has(op));

  if (params.op !== undefined && operator === undefined && st.status !== 'loading' && st.status !== 'idle') {
    return (
      <main class="content">
        <Breadcrumbs items={[{ label: 'All logs', href: href('/logs') }, { label: params.op }]} />
        <h1>Unknown operator</h1>
        <p class="muted">No operator in the current log lists is called <code>{params.op}</code>.</p>
        <a class="btn" href={href('/logs')}><Icon name="arrowLeft" /> All logs</a>
      </main>
    );
  }
  return (
    <main class="content">
      {operator !== undefined && <Breadcrumbs items={[{ label: 'All logs', href: href('/logs') }, { label: operator }]} />}
      {operator === undefined && <section class="hero" style="padding-top:0.5rem">
        <h1>Explore Certificate Transparency logs</h1>
        <p>Read tree heads, browse entries, decode certificates and verify Merkle proofs — straight from the logs, all verified in your browser. Pick a log on the left, or start with one of these:</p>
        <div class="grid-3" style="margin-top:1rem">
          <a class="card action-card" href={href('/sct')}>
            <span class="ico"><Icon name="shield" /></span>
            <span><b>Is this certificate in the logs?</b><span>Paste a certificate: its SCTs are verified and an inclusion proof is checked in every log.</span></span>
          </a>
          <a class="card action-card" href={href('/submit')}>
            <span class="ico"><Icon name="upload" /></span>
            <span><b>Submit a chain</b><span>Send a certificate to several logs at once and save the SCTs.</span></span>
          </a>
          {isDesktop() ? (
            <a class="card action-card" href={href('/settings')}>
              <span class="ico"><Icon name="settings" /></span>
              <span><b>Settings</b><span>Log lists, appearance, local data.</span></span>
            </a>
          ) : (
            <a class="card action-card" href={href('/settings')}>
              <span class="ico"><Icon name="settings" /></span>
              <span><b>Reach blocked logs</b><span>{hasProxy.value ? 'Logs that block browsers are read through a proxy.' : proxyOffer.value ? 'Some logs block browsers (CORS); you will be offered the built-in proxy when it is needed.' : 'Some logs block browsers (CORS) — turn on a proxy or use the desktop app.'}</span></span>
            </a>
          )}
        </div>
      </section>}

      <section id="logs">
        <div class="page-head" style="margin-bottom:0.75rem">
          <div>
            <h2 style="font-size:1.2rem">{operator !== undefined ? <>{operator} <span class="muted" style="font-weight:500">· {groups[0]?.[1].length ?? 0} logs</span></> : 'All logs'}</h2>
            <p class="small muted" style="margin:0.2rem 0 0">
              {st.results.map((r) => (
                <span key={r.source.id} style="margin-right:0.9rem">
                  {r.list ? <Icon name="check" class="muted" size={12} /> : <Icon name="x" size={12} />} {r.source.label}
                  {r.list && <> · {r.list.descriptors.length} logs{r.list.version && `, v${r.list.version}`}{r.list.timestamp && `, ${fmtRelative(Date.parse(r.list.timestamp))}`}{r.list.fromCache && ' (cached)'}</>}
                  {r.error && <span class="muted"> · failed: {r.error}</span>}
                </span>
              ))}
              {st.status === 'loading' && <Spinner label="Loading log lists…" />}
            </p>
          </div>
          <div class="toolbar">
            <input class="input input-sm" style="width:240px" type="search" placeholder="Filter by name, operator or URL" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} aria-label="Filter logs" />
            <span class="seg-field">
              Kind
              <Segmented name="kind" value={kind} onChange={setKind} label="Log kind" size="sm" options={[{ value: 'all', label: 'All' }, { value: 'rfc6962', label: 'RFC 6962', title: 'Classic logs with the /ct/v1/ JSON API' }, { value: 'tiled', label: 'Tiled', title: 'static-ct-api logs served as tiles' }]} />
            </span>
            <label class="check"><input type="checkbox" checked={showRetired} onChange={(e) => updateSettings({ showRetired: (e.target as HTMLInputElement).checked })} /> include retired / rejected ({all - active})</label>
            <button type="button" class="btn btn-sm" onClick={() => setShowAdd((v) => !v)}><Icon name={showAdd ? 'x' : 'link'} /> {showAdd ? 'Close' : 'Add custom log'}</button>
            <button type="button" class="btn btn-ghost btn-sm" onClick={() => void ensureRegistry(true)} disabled={st.status === 'loading'} title="Re-download the log lists"><Icon name="refresh" /> Refresh lists</button>
          </div>
        </div>
        {showAdd && <AddCustomLog onDone={() => setShowAdd(false)} />}
        {st.status === 'error' && <ErrorBox error={st.results.map((r) => r.error).filter(Boolean).join('; ')} title="Could not load any log list" />}
        <div class="row small muted" style="justify-content:space-between;margin-bottom:0.5rem">
          <span>{total} of {all} logs shown in {groups.length} operator groups · states per program: {Object.values(PROGRAM_LABEL).slice(0, 2).join(' and ')} (Firefox follows Chrome's list)</span>
          {groups.length > 0 && (
            <button type="button" class="linklike" onClick={() => setAllOperators(groups.map(([op]) => op), !allCollapsed)}>
              <Icon name={allCollapsed ? 'expandAll' : 'collapseAll'} size={13} /> {allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
            </button>
          )}
        </div>

        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th class="col-log">Log</th>
                <th class="col-kind">Kind</th>
                <th>State</th>
                <th class="col-expiry">Accepts expiry</th>
                <th class="col-open"></th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([op, logs]) => {
                const isOpen = searching || !collapsed.has(op);
                return (
                <>
                  <tr key={op} class="group-row clickable" onClick={() => toggleOperator(op)}>
                    <td colSpan={5}>
                      <span class={`group-head ${isOpen ? 'open' : ''}`} role="button" aria-expanded={isOpen}>
                        <Icon name="chevronRight" class="chev" size={14} />
                        <strong>{op}</strong> <span class="muted small">· {logs.length} log{logs.length === 1 ? '' : 's'}</span>
                        {!isOpen && <span class="muted small" style="margin-left:0.5rem">{logs.filter(isActiveLog).length} active</span>}
                        {operator === undefined && <a class="small group-link" href={operatorPath(op)} onClick={(e) => e.stopPropagation()} title={`Only ${op}'s logs`}>open <Icon name="chevronRight" size={12} /></a>}
                      </span>
                    </td>
                  </tr>
                  {isOpen && logs.map((l) => (
                    <tr key={l.id} class="clickable" onClick={() => navigate(logRoute(l))}>
                      <td class="col-log">
                        <a href={logPath(l)} onClick={(e) => e.stopPropagation()}><strong>{l.description}</strong></a>
                        <div class="tiny muted mono"><UrlCompact url={l.kind === 'tiled' ? l.monitoringUrl : l.url} /></div>
                      </td>
                      <td class="col-kind"><KindBadge kind={l.kind} /></td>
                      <td><ProgramBadges log={l} /></td>
                      <td class="col-expiry small nowrap muted">{l.temporalInterval ? `${l.temporalInterval.start.slice(0, 10)} → ${l.temporalInterval.end.slice(0, 10)}` : '—'}</td>
                      <td class="col-open nowrap" onClick={(e) => e.stopPropagation()}>
                        {l.custom ? (
                          <button type="button" class="btn btn-sm btn-ghost btn-danger" onClick={() => removeCustomLog(l.url)}>Remove</button>
                        ) : (
                          <a class="btn btn-sm btn-ghost" href={logPath(l)}>Open <Icon name="chevronRight" /></a>
                        )}
                      </td>
                    </tr>
                  ))}
                </>
                );
              })}
            </tbody>
          </table>
          {st.status === 'ready' && total === 0 && <div class="empty">No logs match your filter.</div>}
        </div>
      </section>
    </main>
  );
}

function AddCustomLog({ onDone }: { onDone: () => void }) {
  const [kind, setKind] = useState<LogKind>('rfc6962');
  const [url, setUrl] = useState('');
  const [mon, setMon] = useState('');
  const [key, setKey] = useState('');
  const [desc, setDesc] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const d = await addCustomLog({ kind, url, description: desc, ...(mon ? { monitoringUrl: mon } : {}), ...(key ? { key } : {}) });
      onDone();
      navigate(logRoute(d));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form class="card" onSubmit={submit} style="margin-bottom:1rem">
      <div class="card-head"><h2>Add a custom log</h2><span class="small muted">Stored in this browser only</span></div>
      <div class="card-body form-grid">
        <div class="field">
          <span>Kind</span>
          <Segmented name="ckind" value={kind} onChange={setKind} label="Log kind" options={[{ value: 'rfc6962', label: 'RFC 6962' }, { value: 'tiled', label: 'Tiled (static-ct-api)' }]} />
        </div>
        <label class="field">
          <span>Name <span class="hint">optional</span></span>
          <input class="input" placeholder="My test log" value={desc} onInput={(e) => setDesc((e.target as HTMLInputElement).value)} />
        </label>
        <label class="field">
          <span>{kind === 'tiled' ? 'Submission prefix URL' : 'Log URL'}</span>
          <input class="input mono" required placeholder={kind === 'tiled' ? 'https://example.ct.log/' : 'https://ct.example.com/logs/mylog/'} value={url} onInput={(e) => setUrl((e.target as HTMLInputElement).value)} />
        </label>
        {kind === 'tiled' ? (
          <label class="field">
            <span>Monitoring prefix URL <span class="hint">defaults to the submission prefix</span></span>
            <input class="input mono" placeholder="https://mon.example.ct.log/" value={mon} onInput={(e) => setMon((e.target as HTMLInputElement).value)} />
          </label>
        ) : <span />}
        <label class="field span2">
          <span>Public key <span class="hint">base64 SubjectPublicKeyInfo — optional, needed to verify signatures</span></span>
          <input class="input mono" placeholder="MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE…" value={key} onInput={(e) => setKey((e.target as HTMLInputElement).value)} />
        </label>
        {error && <div class="errorbox span2">{error}</div>}
        <div class="form-actions span2" style="margin-top:0">
          <button type="submit" class="btn btn-primary" disabled={busy}>Add log</button>
          <button type="button" class="btn btn-ghost" onClick={onDone}>Cancel</button>
        </div>
      </div>
    </form>
  );
}
